import React, { useState } from 'react';
import { Dialog } from 'primereact/dialog';
import ReusableForm from '../../common/ReusableForm';

/**
 * Dialog component for confirming equipment activity mapping deletion
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.visible - Controls dialog visibility
 * @param {Function} props.onHide - Handler when dialog is closed
 * @param {Object} props.activity - The activity being deleted
 * @param {Function} props.onDelete - Handler for successful deletion
 */
const DeleteEquipmentActivityMappingDialog = ({ visible, onHide, activity, onDelete }) => {
  const [formData, setFormData] = useState({
    confirmation: ''
  });
  
  // Form field configuration
  const deleteFormFields = [
    {
      name: "confirmation",
      label: "Confirmation",
      type: "text",
      required: true,
      placeholder: "Type 'DELETE' to confirm",
      helpText: "This action cannot be undone. Type 'DELETE' to confirm deletion."
    }
  ];
  
  /**
   * Handles form submission and triggers deletion if confirmed
   */
  const handleSubmit = () => {
    if (formData.confirmation === 'DELETE') {
      onDelete();
      setFormData({ confirmation: '' });
    }
  };
  
  /**
   * Reset form data when dialog is closed
   */
  const handleHide = () => {
    setFormData({ confirmation: '' });
    onHide();
  };

  return (
    <Dialog
      visible={visible}
      header="Delete Activity"
      className="delete-dialog max-w-md w-full mx-auto border border-gray-300 rounded-lg shadow-lg"
      onHide={handleHide}
      modal
      closeOnEscape
      dismissableMask
    >
      <div className="p-6 border-t border-gray-200">
        <div className="flex flex-col items-center text-center mb-6">
          <div className="h-16 w-16 rounded-full bg-red-100 flex items-center justify-center mb-4">
            <i className="pi pi-exclamation-triangle text-3xl text-red-500" />
          </div>
          
          <h3 className="text-xl font-semibold text-gray-800 mb-2">
            Are you sure?
          </h3>
          
          <p className="text-gray-600 max-w-sm">
            This action cannot be undone. All data associated with this activity will be permanently removed.
          </p>
        </div>
        
        {activity && (
          <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mb-5">
            <p className="font-medium text-gray-800 mb-1">
              Activity: <span className="text-blue-600">{activity.name}</span>
            </p>
            <p className="text-sm text-gray-600">
              Please review carefully before confirming deletion.
            </p>
          </div>
        )}
        
        <ReusableForm
          formData={formData}
          setFormData={setFormData}
          fields={deleteFormFields}
          onSubmit={handleSubmit}
          onHide={handleHide}
          loading={false}
          submitLabel="Delete Activity"
          submitButtonClass="bg-red-600 hover:bg-red-700 text-white font-medium px-6 py-2.5 rounded-md border-2 border-red-700 shadow-md transition-all duration-200 focus:ring-2 focus:ring-red-500 focus:ring-opacity-50"
          cancelButtonClass="bg-white hover:bg-gray-100 text-gray-800 font-medium px-6 py-2.5 rounded-md border-2 border-gray-300 shadow-md transition-all duration-200 focus:ring-2 focus:ring-gray-300 focus:ring-opacity-50 mr-3"
        />
      </div>
    </Dialog>
  );
};

export default DeleteEquipmentActivityMappingDialog;